import React, { useState, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import {
  Grid,
  TextField,
  Skeleton,
  InputAdornment,
  IconButton,
  Divider,
  Typography,
  Button,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useApiGet, useApiSend } from '../../../utils/httpClientTs';
import {
  getLocalChapterIRSFormDetail,
  getSPDIRSFormDetail,
  getReciprocityIRSFormDetail,
  updateLocalChapterIRSForm,
  updateSPDIRSForm,
  updateReciprocityIRSForm,
} from '../../../endpoints/irsForms';
import { UpdateIRSFormRequest } from '../../../types/irsForms';
import IRSFormSections from './IRSFormSections';
import PageHeader from '../../../Components/PageHeader';
import AlertItem from '../../../Components/AlertItem';
import UserSearchModal from '../../../Components/UserSearchModal';
import { applications } from '../../../utils/variables';

const IRSFormsDetail: React.FC = () => {
  const { id, year } = useParams<{
    id: string;
    year: string;
  }>();
  const location = useLocation();

  const [hideContent, setHideContent] = useState(true);
  const [assignedEdit, setAssignedEdit] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState<
    'success' | 'error' | 'warning' | 'info'
  >('success');
  const [openUserSelect, setOpenUserSelect] = useState(false);
  const [editingApproverStatusId, setEditingApproverStatusId] = useState<
    string | null
  >(null);

  // Determine form type from URL path
  const actualFormType: 'lc' | 'sc' | 'rg' = useMemo(() => {
    const path = location.pathname;
    if (path.includes('/local-chapters/')) {
      return 'lc';
    } else if (path.includes('/spd/')) {
      return 'sc';
    } else if (path.includes('/reciprocity/')) {
      return 'rg';
    }
    return 'lc';
  }, [location.pathname]);

  // Get appropriate detail function based on form type
  const getDetailFunction = () => {
    switch (actualFormType) {
      case 'lc':
        return getLocalChapterIRSFormDetail;
      case 'sc':
        return getSPDIRSFormDetail;
      case 'rg':
        return getReciprocityIRSFormDetail;
      default:
        return getLocalChapterIRSFormDetail;
    }
  };

  // Get appropriate update function based on form type
  const getUpdateFunction = () => {
    switch (actualFormType) {
      case 'lc':
        return updateLocalChapterIRSForm;
      case 'sc':
        return updateSPDIRSForm;
      case 'rg':
        return updateReciprocityIRSForm;
      default:
        return updateLocalChapterIRSForm;
    }
  };

  const { data, isLoading, refetch } = useApiGet(
    ['irs-form-detail', id, actualFormType],
    () => getDetailFunction()(id!),
    {
      enabled: !!id,
      retry: 1,
      refetchInterval: 15 * 60 * 1000, // Refetch every 15 minutes
    }
  );

  const { mutate: updateForm } = useApiSend(
    (data: UpdateIRSFormRequest) => getUpdateFunction()(id!, data),
    () => {
      setAlertMessage('IRS Form successfully updated.');
      setAlertSeverity('success');
      setShowAlert(true);
      refetch();
    },
    (error) => {
      console.error(error);
      setAlertMessage(
        'Error updating IRS Form. If the problem persists, please contact support.'
      );
      setAlertSeverity('error');
      setShowAlert(true);
    }
  );

  const handleSetAssigned = () => {
    setAssignedEdit(true);
    setEditingApproverStatusId(null);
    setOpenUserSelect(true);
  };

  const handleSetApprover = (statusId: string) => {
    setEditingApproverStatusId(statusId);
    setAssignedEdit(false);
    setOpenUserSelect(true);
  };

  const onAssignedUpdate = (assigned: string) => {
    setOpenUserSelect(false);
    if (assigned === data?.assigned_to_id || assigned === '0') {
      return;
    }

    if (assignedEdit) {
      updateForm({
        assignee_id: assigned,
      });
    } else if (editingApproverStatusId) {
      updateForm({
        approvers: [
          {
            status_id: editingApproverStatusId,
            approver_id: assigned,
          },
        ],
      });
    }
  };

  const allowEdit = () => {
    return data?.assigned_to_id === localStorage.getItem('user_id');
  };

  const isARAdmin = () => {
    return (
      localStorage.getItem('apps')?.includes(applications.AnnualReportAdmin) ||
      false
    );
  };

  const getChapterName = () => {
    if (actualFormType === 'sc' && data?.spd_chapter_name) {
      return data.spd_chapter_name;
    }
    if (actualFormType === 'rg' && data?.reciprocity_name) {
      return data.reciprocity_name;
    }
    return data?.local_chapter_name || '';
  };

  const getFormTypeLabel = () => {
    if (actualFormType === 'sc') return 'SPD';
    if (actualFormType === 'rg') return 'Reciprocity';
    return 'Local Chapter';
  };

  const handleApprove = () => {
    refetch();
    setAlertMessage('Form approved successfully.');
    setAlertSeverity('success');
    setShowAlert(true);
  };

  const handleReject = () => {
    refetch();
    setAlertMessage('Form rejected successfully.');
    setAlertSeverity('success');
    setShowAlert(true);
  };

  if (isLoading) {
    return <Skeleton variant="rectangular" height={500} />;
  }

  if (!data) {
    return <Typography>Form not found</Typography>;
  }

  return (
    <Grid container>
      <Grid item xs={12}>
        {showAlert && (
          <AlertItem
            severity={alertSeverity}
            description={alertMessage}
            onClose={() => setShowAlert(false)}
          />
        )}
      </Grid>
      <Grid item xs={12}>
        <PageHeader
          title={`${getChapterName()} - ${getFormTypeLabel()} IRS Form (${year})`}
          leftDom={null}
          subtitle=""
          rightDom={
            isARAdmin() ? (
              <Button
                variant="outlined"
                onClick={() => setHideContent(!hideContent)}
              >
                {hideContent ? 'Show Info' : 'Hide Info'}
              </Button>
            ) : null
          }
        />
      </Grid>
      {isARAdmin() && !hideContent && (
        <Grid item xs={12}>
          <Grid container>
            <Grid item xs={4} md={3} padding={2}>
              <TextField
                id="chapter-name"
                label="Chapter"
                InputProps={{
                  readOnly: true,
                }}
                fullWidth
                value={getChapterName()}
                variant="standard"
              />
            </Grid>
            <Grid item xs={4} md={3} padding={2}>
              <TextField
                id="form-type"
                label="Form Type"
                fullWidth
                InputProps={{
                  readOnly: true,
                }}
                value={data.irs_type_name}
                variant="standard"
              />
            </Grid>
            <Grid item xs={4} md={3} padding={2}>
              <TextField
                id="current-status"
                label="Current Status"
                fullWidth
                value={data.status_name}
                InputProps={{ readOnly: true }}
                variant="standard"
              />
            </Grid>
            <Grid item xs={4} md={3} padding={2}>
              <TextField
                id="assigned-to"
                label="Assigned To"
                fullWidth
                value={data.assigned_to || ''}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        color="primary"
                        onClick={handleSetAssigned}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                variant="standard"
              />
            </Grid>
          </Grid>
          <Grid item xs={12} padding={2}>
            <Divider>
              <Typography
                variant="overline"
                component="div"
                textAlign={'center'}
                color="primary"
              >
                Approvers
              </Typography>
            </Divider>
          </Grid>
          <Grid item container>
            {data.approvers.map((approver) => (
              <Grid item xs={4} md={3} padding={2} key={approver.status_id}>
                <TextField
                  id={`approver-${approver.status_id}`}
                  label={approver.status_name}
                  fullWidth
                  value={approver.user_name}
                  variant="standard"
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          color="primary"
                          onClick={() => handleSetApprover(approver.status_id)}
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
      )}
      {data && (
        <Grid item xs={12}>
          <IRSFormSections
            formId={data.id}
            formDetail={data}
            editable={allowEdit()}
            formType={actualFormType}
            onApprove={handleApprove}
            onReject={handleReject}
          />
        </Grid>
      )}
      <UserSearchModal
        modalOpen={openUserSelect}
        setModalOpen={setOpenUserSelect}
        setUser={onAssignedUpdate}
        statusFilter="active"
      />
    </Grid>
  );
};

export default IRSFormsDetail;
